import React from 'react';
import styled from 'styled-components';
import { 
  PieChart as RechartsPie, 
  Pie, 
  Cell, 
  ResponsiveContainer, 
  Tooltip 
} from 'recharts';

const ChartWrapper = styled.div`
  width: 100%;
  height: ${props => props.$height || '200px'};
`;

const TooltipContainer = styled.div`
  background: #1f2937;
  border: 1px solid #374151;
  border-radius: 8px;
  padding: 10px 14px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
`;

const TooltipLabel = styled.div`
  font-size: 12px;
  color: #9ca3af;
  margin-bottom: 4px;
`;

const TooltipValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #fff;
`;

const TooltipPercent = styled.div`
  font-size: 11px;
  color: #10b981;
  margin-top: 2px;
`;

const CustomLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-top: 8px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #d1d5db;
`;

const LegendDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.$color};
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #6b7280;
  font-size: 13px;
`;

const DEFAULT_COLORS = ['#8b5cf6', '#f59e0b', '#10b981', '#06b6d4', '#ec4899', '#3b82f6'];

const CustomTooltip = ({ active, payload, formatValue }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <TooltipContainer>
        <TooltipLabel>{data.name}</TooltipLabel>
        <TooltipValue>{formatValue ? formatValue(data.value) : data.value}</TooltipValue>
        {data.percentage !== undefined && (
          <TooltipPercent>{data.percentage}%</TooltipPercent>
        )}
      </TooltipContainer>
    );
  }
  return null;
};

/**
 * PieChart Component
 * 
 * @param {Array} data - Array of { name: string, value: number, color?: string }
 * @param {string} height - Chart height (default: '200px')
 * @param {Array} colors - Array of colors (default: purple, amber, emerald, cyan, pink, blue)
 * @param {boolean} showLegend - Show legend below chart (default: true)
 * @param {boolean} showPercentage - Show percentage in legend (default: true)
 * @param {boolean} donut - Donut style with inner radius (default: true)
 * @param {string} innerRadius - Inner radius (default: '50%')
 * @param {string} outerRadius - Outer radius (default: '80%')
 * @param {Function} formatValue - Custom value formatter for tooltip
 * @param {string} emptyMessage - Message when no data (default: 'No data')
 */
const PieChart = ({ 
  data = [],
  height = '200px',
  colors = DEFAULT_COLORS,
  showLegend = true,
  showPercentage = true,
  donut = true,
  innerRadius = '50%',
  outerRadius = '80%',
  formatValue,
  emptyMessage = 'No data'
}) => {
  // Filter out zero values and calculate percentages
  const chartData = React.useMemo(() => {
    const filtered = data.filter(item => item.value > 0);
    const total = filtered.reduce((sum, item) => sum + item.value, 0);
    
    return filtered.map((item, index) => ({
      ...item,
      color: item.color || colors[index % colors.length],
      percentage: total > 0 ? ((item.value / total) * 100).toFixed(1) : 0
    }));
  }, [data, colors]);

  if (chartData.length === 0) {
    return (
      <ChartWrapper $height={height}>
        <EmptyState>{emptyMessage}</EmptyState>
      </ChartWrapper>
    );
  }

  return (
    <ChartWrapper $height={height}>
      <ResponsiveContainer width="100%" height={showLegend ? '75%' : '100%'}>
        <RechartsPie>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={donut ? innerRadius : 0}
            outerRadius={outerRadius}
            paddingAngle={2}
            dataKey="value"
            stroke="none"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip formatValue={formatValue} />} />
        </RechartsPie>
      </ResponsiveContainer>
      {showLegend && (
        <CustomLegend>
          {chartData.map((entry) => (
            <LegendItem key={entry.name}>
              <LegendDot $color={entry.color} />
              {entry.name}{showPercentage && ` (${entry.percentage}%)`}
            </LegendItem>
          ))}
        </CustomLegend>
      )}
    </ChartWrapper>
  );
};

export default PieChart;
